import * as React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { Capability } from "@steelbill/types";
import { useAuth } from "@/features/auth/auth-context";
import { routes } from "@/routes/paths";

/**
 * Route guards (spec §2.1, §11).
 *
 * Two guards, and the distinction between them matters:
 *
 *   {@link ProtectedRoute}  — "are you signed in?"     → redirect to login
 *   {@link RequireCapability} — "may you do this?"     → redirect to Home
 *
 * A signed-out user is sent to the login screen WITH the URL he was trying to
 * reach, so a WhatsApp link to an invoice survives the sign-in. A signed-IN
 * user who lacks a capability is sent Home, not to login — asking him to sign
 * in again would suggest the credentials are the problem when the answer is
 * that this screen is not his (§2.1: OWNER has no `settings.write`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NEITHER GUARD LOOKS AT A ROLE STRING.
 *
 * §2.1: "Never write `if (user.role === 'ADMIN')` in a route handler or a
 * component." `RequireCapability` takes a {@link Capability} — checked at
 * compile time against the union in `packages/types` — and resolves it through
 * `can()`, the same function `requireCap` uses on the server. Adding the
 * ACCOUNTANT role later is a data change in `permissions.ts` and nothing here
 * changes.
 *
 * And this is UX, not security (§2.1: "hiding a button is not security"). The
 * API refuses independently and audits the refusal; these guards exist so the
 * owner never taps into a screen that would only tell him no.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export function ProtectedRoute(): React.JSX.Element {
  const { signedIn, ready } = useAuth();
  const location = useLocation();

  // `ready` is synchronous today (the session is read during the provider's
  // first render, deliberately — see auth-context.tsx). It is honoured anyway
  // so that a future async bootstrap cannot introduce a one-frame redirect that
  // bounces a signed-in owner through the PIN screen.
  if (!ready) return <></>;

  if (!signedIn) {
    return (
      <Navigate
        to={routes.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <Outlet />;
}

/**
 * Wraps a route (or a nested route tree) that needs a capability.
 *
 * ```tsx
 * <Route element={<RequireCapability capability="settings.write" />}>
 *   <Route path="settings" element={<SettingsScreen />} />
 * </Route>
 * ```
 */
export function RequireCapability({
  capability,
  children,
  fallback = routes.home,
}: {
  capability: Capability;
  /** Omit to guard nested routes via `<Outlet />`. */
  children?: React.ReactNode;
  fallback?: string;
}): React.JSX.Element {
  const { can, signedIn, ready } = useAuth();
  const location = useLocation();

  if (!ready) return <></>;

  if (!signedIn) {
    return (
      <Navigate
        to={routes.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!can(capability)) return <Navigate to={fallback} replace />;

  return <>{children ?? <Outlet />}</>;
}

/**
 * Wraps a route that ANY ONE of several capabilities opens.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS STILL CAPABILITIES AND NEVER A ROLE (§2.1) — this is `can(a) || can(b)`,
 * spelled once, not a shortcut around the map.
 *
 * IT EXISTS FOR THE MESSENGER'S LIST. `/messages` shows the people you may write
 * to privately (`message.direct`) AND the shared room (`message.view`), and the
 * two grants are genuinely independent: §2.1 keeps `message.direct` separate
 * precisely so an outside professional can be confined to the room where
 * everything is said in front of everyone. So the list is worth opening for
 * somebody holding EITHER, and each row inside it is still gated on its own
 * capability — as is the route the row leads to. Requiring both would hide the
 * room from a future person granted only private conversations; requiring
 * `message.view` alone would do the reverse.
 *
 * A ROUTE THAT NEEDS BOTH IS STILL TWO NESTED `RequireCapability` WRAPPERS, and
 * there is deliberately no `all` variant here: `||` and `&&` reading the same on
 * the page is how the wrong one gets used.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function RequireAnyCapability({
  capabilities,
  children,
  fallback = routes.home,
}: {
  capabilities: readonly Capability[];
  /** Omit to guard nested routes via `<Outlet />`. */
  children?: React.ReactNode;
  fallback?: string;
}): React.JSX.Element {
  const { can, signedIn, ready } = useAuth();
  const location = useLocation();

  if (!ready) return <></>;

  if (!signedIn) {
    return (
      <Navigate
        to={routes.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!capabilities.some((capability) => can(capability))) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * Render `children` only if the capability is held.
 *
 * The component form of `useCan`, for hiding a nav item or an action inside a
 * screen that is otherwise permitted. Prefer DISABLING a control with an
 * explanation over hiding it when the owner might reasonably look for it; hide
 * it when its absence is not surprising (the ADMIN-only Settings entry).
 */
export function Can({
  capability,
  children,
  otherwise = null,
}: {
  capability: Capability;
  children: React.ReactNode;
  otherwise?: React.ReactNode;
}): React.JSX.Element {
  const { can } = useAuth();
  return <>{can(capability) ? children : otherwise}</>;
}
